import LocalShipping from '@mui/icons-material/LocalShipping';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  Account_Read_Typeahead,
  AccountType,
  getV1CompaniesAccountsTypeahead,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect } from 'react';
import { Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';

import { TextFormField } from '~components/FormFields/TextFormField';
import AsyncAutocompleteField from '~components/FormFields/v2/Autocomplete/AsyncAutocompleteField';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { AccountTypeahead } from '~hooks/useAccount/models';
import { usePrevious } from '~utils/hooks/usePrevious';

import { EquipmentFormValues } from '../EquipmentForm';

export const ACTING_COMPANY_OWNER_VALUE = '';

type OwnerOption = AutocompleteFieldOption<AccountTypeahead | null>;

interface OwnedByFieldProps {
  actingCompanyName: string;
  companyId: string;
  control: Control<EquipmentFormValues>;
  errors: FieldErrors<EquipmentFormValues>;
  setValue: UseFormSetValue<EquipmentFormValues>;
}

export default function OwnedByField({
  actingCompanyName,
  companyId,
  control,
  errors,
  setValue,
}: OwnedByFieldProps) {
  const ownedByAccount = useWatch({ control, name: 'ownedByAccount' });
  const previousOwnerValue = usePrevious(ownedByAccount?.value);
  const selectedVendorName = ownedByAccount?.value ? ownedByAccount.label : null;

  useEffect(() => {
    if (
      previousOwnerValue !== undefined &&
      previousOwnerValue !== ownedByAccount?.value
    ) {
      setValue('shareName', '');
      setValue('shareTicketMatchId', '');
    }
  }, [ownedByAccount?.value, previousOwnerValue, setValue]);

  const extraOptions: OwnerOption[] = [
    { _entity: null, label: actingCompanyName, value: ACTING_COMPANY_OWNER_VALUE },
  ];

  const parseFn = (item: Account_Read_Typeahead): OwnerOption => {
    const parsed = AccountTypeahead.parse(item);

    return {
      _entity: parsed,
      label: parsed.name,
      value: parsed.connectedCompanyId ?? '',
    };
  };

  const groupBy = (option: OwnerOption) =>
    option.value === ACTING_COMPANY_OWNER_VALUE
      ? t('administration.equipment.owned_by_group_your_company')
      : t('administration.equipment.owned_by_group_connected_vendors');

  return (
    <Box sx={{ mt: 2 }}>
      <AsyncAutocompleteField
        control={control}
        disableClearable
        extraOptions={extraOptions}
        groupBy={groupBy}
        isRequired
        label={t('administration.equipment.owned_by')}
        name="ownedByAccount"
        parseFn={parseFn}
        queryName="EquipmentOwnerAccountsTypeahead"
        fetchFn={{
          arguments: {
            path: { 'company-id': companyId },
            query: {
              'filter[account_types]': [AccountType.VENDOR],
              'filter[connected]': true,
            },
          },
          request: getV1CompaniesAccountsTypeahead,
        }}
      />

      <Typography
        variant="body2"
        sx={(theme) => ({
          color: theme.semantic.textSecondary,
          fontSize: '12px',
          mt: 0.5,
        })}
      >
        {t('administration.equipment.owned_by_hint')}
      </Typography>

      {selectedVendorName && (
        <Box
          sx={(theme) => ({
            backgroundColor: theme.semantic.surfaceSunken,
            border: `1px solid ${theme.semantic.border}`,
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            mt: 1,
            p: 1.5,
          })}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <LocalShipping
              sx={(theme) => ({ color: theme.semantic.textSecondary, fontSize: '18px' })}
            />
            <Typography variant="body2" sx={{ fontSize: '13px' }}>
              {t('administration.equipment.owned_by_vendor_consequence', {
                company: selectedVendorName,
                interpolation: { escapeValue: false },
              })}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ width: '50%' }}>
              <TextFormField
                control={control}
                errors={errors}
                name="shareName"
                label={`${t('administration.equipment.owner_share_name')}`}
                isRequired={false}
              />
            </Box>
            <Box sx={{ width: '50%' }}>
              <TextFormField
                control={control}
                errors={errors}
                name="shareTicketMatchId"
                label={`${t('administration.equipment.owner_share_ticket_match_id')}`}
                isRequired={false}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
